/**
 * Butter Tart Selection Modal
 *
 * When a customer adds Butter Tart or 6 Butter Tarts, this modal opens so they
 * can choose Plain or Pecan (or other options from Product Options for Tarts).
 * Stores selection in cart item notes, e.g. "Flavor: Pecan".
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const API_URL = import.meta.env.VITE_API_URL || '';

const FALLBACK_FLAVORS = ['Plain', 'Pecan'];

interface ButterTartSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: string) => void;
  productId: string;
  productName: string;
  productPrice: number;
  /** When opening from "Change flavour" in cart, pass current notes to pre-fill */
  initialNotes?: string;
  /** When true, show "Update flavour" instead of "Add to Cart" (cart edit mode) */
  isEditMode?: boolean;
}

function parseNotesToFlavor(notes: string): string {
  if (!notes?.trim()) return '';
  const m = notes.match(/^\s*Flavor:\s*(.+)$/i);
  return m ? m[1].trim() : '';
}

export default function ButterTartSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  productId: _productId,
  productName,
  productPrice,
  initialNotes,
  isEditMode = false,
}: ButterTartSelectionModalProps) {
  const [flavors, setFlavors] = useState<string[]>(FALLBACK_FLAVORS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFlavor, setSelectedFlavor] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const fetchFlavors = async () => {
      setLoading(true);
      setError(null);
      try {
        const base = API_URL ? `${API_URL}/api/product-options` : '/api/product-options';
        const response = await fetch(`${base}?category=Tarts`);
        if (!response.ok) throw new Error(`API returned ${response.status}`);
        const data = await response.json();

        let flavorGroup = data.groups?.find(
          (g: { name: string }) => g.name?.toLowerCase().includes('flavor') || g.name?.toLowerCase().includes('variety')
        );
        if (!flavorGroup && data.groups?.length > 0) {
          flavorGroup = data.groups.find(
            (g: { category_name: string }) => g.category_name === 'Tarts'
          );
        }
        if (!flavorGroup && data.groups?.length > 0) flavorGroup = data.groups[0];

        if (flavorGroup?.options?.length > 0) {
          const available = flavorGroup.options.filter(
            (opt: { is_available: boolean }) => opt.is_available !== false
          );
          if (available.length > 0) {
            setFlavors(available.map((opt: { name: string }) => opt.name));
          }
        }
      } catch (err) {
        console.error('Failed to fetch butter tart flavors:', err);
        setError('Using default options.');
        setFlavors(FALLBACK_FLAVORS);
      } finally {
        setLoading(false);
      }
    };

    fetchFlavors();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFlavor('');
      setError(null);
    } else if (initialNotes) {
      setSelectedFlavor(parseNotesToFlavor(initialNotes));
    }
  }, [isOpen, initialNotes]);

  const isValid = selectedFlavor !== '';

  const handleConfirm = () => {
    if (!isValid) {
      alert('Please select a flavour (Plain or Pecan).');
      return;
    }
    onConfirm(`Flavor: ${selectedFlavor}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full">
          <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-display font-bold text-gray-900">
                Choose Your Flavour
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isEditMode ? 'Change your selection below, then click Update.' : 'Pick Plain or Pecan below, then click Add to Cart.'}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">{productName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-600">Loading options...</p>
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800">{error}</p>
                  </div>
                )}
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flavour <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedFlavor}
                  onChange={(e) => setSelectedFlavor(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  aria-label="Choose butter tart flavour (Plain or Pecan)"
                  title="Choose butter tart flavour"
                >
                  <option value="">Choose Plain or Pecan...</option>
                  {flavors.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              Total: <span className="font-semibold text-gray-900">${productPrice.toFixed(2)}</span>
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!isValid}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isEditMode ? 'Update flavour' : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
